import { watch } from 'vue';
import { useArrayDifference } from '@vueuse/core';

import TripPlannerModel from '@apps/main/modules/tripPlannerModel/TripPlannerModel';
import {
  useEditingIngredientStore,
  useMapStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useCriteriaStore } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';
import { useStaticDataStore } from '@apps/main/new-desktop/core/stores/staticData/staticDataStore';

import type { HotelConstraintQL } from '@libs/trip-planner-interfaces';
import { stringComparer } from '@libs/utils';

import type { HotelIngredient } from '../recipe/ingredients';
import { adaptHotelIngredientToHotelConstraintQL } from '../criteria/ingredientAdapters/activitiesInDestinationSearchAdapter/activitiesInDestinationSearchAdapter';
import type { OwnStayIngredient } from '../recipe/ingredients/ownStayIngredient';

import { useItineraryViewModel } from './ItineraryViewModel';
import { useDestinationAvailabilityViewModel } from './DestinationAvailabilityViewModel';
import { useProductAvailabilityViewModel } from './ProductAvailabilityViewModel';
import { useRecipeViewModel } from './RecipeViewModel';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useTripPlannerViewModel() {
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();
  const mapStore = useMapStore();
  const criteriaStore = useCriteriaStore();

  const staticDataStore = useStaticDataStore();
  staticDataStore.setAllowMultipleVehiclesToFerryIngredients();

  const editingIngredientStore = useEditingIngredientStore();
  const statsigEventsStore = useStatsigEventsStore();

  const model = new TripPlannerModel(statsigEventsStore);

  useRecipeViewModel(model);
  useItineraryViewModel(model);
  useDestinationAvailabilityViewModel(model);
  useProductAvailabilityViewModel(model);

  watch(
    criteriaStore.searchSuggestionCriteria,
    (criteria) => {
      if ('searchText' in criteria && criteria.searchText === '') {
        return;
      }
      model
        .searchAutoCompleteSuggestions(criteriaStore.searchSuggestionCriteria)
        .subscribe((response) => {
          recipeStore.setSearchSuggestionResults(response);
        });
    },
    { deep: true }
  );

  watch(
    () => criteriaStore.searchActivitiesInDestinationCriteria,
    (newValue, oldValue) => {
      const arrayDifference = useArrayDifference(
        newValue,
        oldValue ?? [],
        (a, b) => stringComparer(a, b) === 0
      );
      arrayDifference.value.forEach((criteria) => {
        const destination = recipeStore.getStayIngredient(
          criteria.ingredientId
        );

        searchActivitiesInDestination(destination);
      });
    },
    { immediate: true, deep: true }
  );

  mapStore.mapViews.subscribe((polygonConstraint) => {
    mapStore.updateMapShouldCenterOnMarkers(false);

    if (!editingIngredientStore.editingStayIngredient) {
      return;
    }

    editingIngredientStore.editingStayIngredient.polygonConstraint =
      polygonConstraint;
  });

  function searchActivitiesInDestination(
    destination: HotelIngredient | OwnStayIngredient
  ): void {
    const constraint: HotelConstraintQL =
      adaptHotelIngredientToHotelConstraintQL(destination);

    const hasOnlyHotelCode =
      !!constraint.hotelCodes?.length &&
      !constraint.polygonId &&
      !constraint.destination;

    if (hasOnlyHotelCode) {
      return;
    }

    model.searchAreThereActivityStaticInfo(constraint).subscribe((response) => {
      tripStore.updateDestinationWithAvailableActivities(
        destination.ingredientId,
        response
      );
    });
  }
}

export type TripPlannerViewModel = ReturnType<typeof useTripPlannerViewModel>;
